import { useMemo } from 'react';

import { SubscriptionUtilsType } from '@web/account/types';
import { useBillingInfo } from '@web/account/billing/BillingInfoContext';

type Props = {
  isYearlyPlan: boolean;
  ignoreNextCharge: boolean;
  subscriptionLevel: number;
  subscriptionUtils: SubscriptionUtilsType;
  taxPercent: number | null;
  isSubtotalWithDiscount?: boolean;
};

export const usePricingBreakdown = ({
  isYearlyPlan,
  ignoreNextCharge,
  subscriptionLevel,
  subscriptionUtils,
  taxPercent,
  isSubtotalWithDiscount = true,
}: Props) => {
  const { billingInfo } = useBillingInfo();

  const priceSummary = useMemo(() => {
    const totalNetPrice = subscriptionUtils.getTotal(
      billingInfo,
      subscriptionLevel,
      ignoreNextCharge,
      isYearlyPlan
    );
    const taxAmount = Math.round(totalNetPrice * taxPercent) / 100;
    const discount = subscriptionUtils.getDiscount(billingInfo, subscriptionLevel, isYearlyPlan);
    // in purchase page we want to display pre-yearly-discount subtotal amount
    // while in billing page subtotal already accounts for the discount
    const subtotalPrice = isSubtotalWithDiscount
      ? subscriptionUtils.getSubtotal(billingInfo, subscriptionLevel, isYearlyPlan)
      : subscriptionUtils.getSubtotal(billingInfo, subscriptionLevel, false) *
        (isYearlyPlan ? 12 : 1);

    return {
      discount,
      taxAmount,
      totalNetPrice,
      totalGrossPrice: totalNetPrice + taxAmount,
      subtotalPrice,
    };
  }, [
    billingInfo,
    isYearlyPlan,
    ignoreNextCharge,
    subscriptionLevel,
    subscriptionUtils,
    taxPercent,
    isSubtotalWithDiscount,
  ]);

  return {
    priceSummary,
  };
};
